import { AloDbBase, IAloDbBaseConstParam } from '@/models/alodb/AloDbBase'
import { AloBaseArray } from '@/models/alo/AloBaseArray'
import { getDefaultSmartSimulation, ISmartSimulation } from '../iris/smartMission/ISmartSimulation'
import {
  getDefaultSmartSimulationCoverage,
  ISmartSimulationCoverage,
} from '../iris/smartMission/ISmartSimulationCoverage'
import { MissionGlobalType } from '@/enum/MissionGlobalType'
import { MissionPlanUiState } from '@/models/ui/MissionPlanUiState'
import { getDefaultGliderConfig } from '../iris/smartMission/IGliderConfig'
import { SimulationMode } from '@/enum/SimulationMode'
import {
  getDefaultSmartResultGridCoverage,
  ISmartResultGridCoverage,
} from '../iris/smartMission/ISmartResultGridCoverage'
import { ISmartSimulationBase } from '../iris/smartMission/ISmartSimulationBase'
import {
  getDefaultSmartSimulationFormation,
  ISmartSimulationFormation,
} from '../iris/smartMission/ISmartSimulationFormation'

export interface IAloDbMissionGlobalConstParam extends IAloDbBaseConstParam {
  MIG_ID: number | null
  MIG_KEY: string | null
  MIG_TYP: number | null
  MIG_LIBELLE: string | null
  MIG_DESC: string | null
  ENS_ID: number | null
  misIdArray: Array<number>
  MIG_JSON: string
  MIG_RESULT_GRID_JSON: string
  ensLibelle: string | null
}

export type AloDbMissionGlobalCoverage = AloDbMissionGlobal & { config: ISmartSimulationCoverage }
export type AloDbMissionGlobalSimulation = AloDbMissionGlobal & { config: ISmartSimulation }
export type AloDbMissionGlobalFormation = AloDbMissionGlobal & { config: ISmartSimulationFormation }

export function isAloDbMissionGlobalCoverage(toBeDetermined: any): toBeDetermined is AloDbMissionGlobalCoverage {
  if (
    (toBeDetermined as AloDbMissionGlobalCoverage).MIG_TYP == MissionGlobalType.COVERAGE ||
    toBeDetermined?.config?.processing_mode == SimulationMode.coverage
  ) {
    return true
  }
  return false
}

export function isAloDbMissionGlobalSimulation(toBeDetermined: any): toBeDetermined is AloDbMissionGlobalSimulation {
  if (
    (toBeDetermined as AloDbMissionGlobalSimulation).MIG_TYP == MissionGlobalType.SIMULATION ||
    toBeDetermined?.config?.processing_mode == SimulationMode.simulator
  ) {
    return true
  }
  return false
}

export function isAloDbMissionGlobalFormation(toBeDetermined: any): toBeDetermined is AloDbMissionGlobalFormation {
  if (
    (toBeDetermined as AloDbMissionGlobalSimulation).MIG_TYP == MissionGlobalType.FORMATION ||
    toBeDetermined?.config?.processing_mode == SimulationMode.formation
  ) {
    return true
  }
  return false
}

export function isAloDbMissionGlobalSimulator(toBeDetermined: any): toBeDetermined is AloDbMissionGlobal {
  switch ((toBeDetermined as AloDbMissionGlobal).MIG_TYP) {
    case MissionGlobalType.SIMULATION:
    case MissionGlobalType.COVERAGE:
    case MissionGlobalType.FORMATION:
      return true
    default:
      return false
  }
}

export class AloDbMissionGlobal extends AloDbBase {
  public MIG_ID: number | null
  public MIG_KEY: string | null
  public MIG_TYP: number | null
  public MIG_LIBELLE: string | null
  public MIG_DESC: string | null
  public ENS_ID: number | null
  public misIdArray: Array<number>
  public MIG_JSON: string | null
  public MIG_RESULT_GRID_JSON: string | null
  public ensLibelle: string | null

  public config: ISmartSimulation | ISmartSimulationCoverage | ISmartSimulationFormation | null
  public resultGridJson: ISmartResultGridCoverage | null

  constructor(
    {
      MIG_ID,
      MIG_KEY,
      MIG_TYP,
      MIG_LIBELLE,
      MIG_DESC,
      ENS_ID,
      MIG_JSON,
      MIG_RESULT_GRID_JSON,
      misIdArray = [],
      ensLibelle,
      ...args
    }: IAloDbMissionGlobalConstParam = {} as IAloDbMissionGlobalConstParam,
  ) {
    super(args)
    this.MIG_ID = MIG_ID
    this.MIG_TYP = MIG_TYP
    this.MIG_KEY = MIG_KEY
    this.MIG_LIBELLE = MIG_LIBELLE
    this.MIG_DESC = MIG_DESC
    this.ENS_ID = ENS_ID
    this.misIdArray = misIdArray
    this.MIG_JSON = MIG_JSON
    this.MIG_RESULT_GRID_JSON = MIG_RESULT_GRID_JSON
    this.ensLibelle = ensLibelle

    if (
      MIG_TYP == MissionGlobalType.SIMULATION ||
      MIG_TYP == MissionGlobalType.COVERAGE ||
      MIG_TYP == MissionGlobalType.FORMATION
    )
      this.config = AloDbMissionGlobal.parseSmartSimulation(MIG_JSON, MIG_TYP)
    else this.config = null
    if (MIG_TYP == MissionGlobalType.COVERAGE)
      this.resultGridJson = AloDbMissionGlobal.parseResultGridCoverage(MIG_RESULT_GRID_JSON)
    else this.resultGridJson = null
  }

  static parseResultGridCoverage(json?: string | null) {
    return AloDbMissionGlobal.parseIrisResultGridCoverage(JSON.parse(json || '{}')?.gr_iris_result)
  }

  static parseIrisResultGridCoverage(json?: string | null) {
    return getDefaultSmartResultGridCoverage(JSON.parse(json || '{}'))
  }

  static parseSmartSimulation(json?: string | null, MIG_TYP?: MissionGlobalType) {
    const conf = JSON.parse(json ?? '{}') as ISmartSimulation

    const fctBuilder =
      MIG_TYP == MissionGlobalType.COVERAGE || conf?.processing_mode == SimulationMode.coverage
        ? getDefaultSmartSimulationCoverage
        : MIG_TYP == MissionGlobalType.FORMATION || conf?.processing_mode == SimulationMode.formation
        ? getDefaultSmartSimulationFormation
        : getDefaultSmartSimulation
    //@ts-ignore
    const config = {
      ...fctBuilder({
        gliders_config: [
          //@ts-ignore
          getDefaultGliderConfig({
            battery_type: conf?.battery_type ?? undefined,
            bms_type: conf?.bms_type ?? undefined,
            payload: conf?.mission_plan?.payload?.map(s => s.snr_label) ?? undefined,
          }),
        ],
      }),
      ...conf,
    } as ISmartSimulation | ISmartSimulationCoverage | ISmartSimulationCoverage

    if (config.gliders_config && !Array.isArray(config.gliders_config)) config.gliders_config = [config.gliders_config]
    //reparse les glider config si des nouveau param ont été ajoutés au model pour avoir les bonnes valeurs par default
    config.gliders_config = config.gliders_config.map(g => {
      //@ts-ignore
      const configGli = {
        ...getDefaultGliderConfig({
          battery_type: conf?.battery_type ?? undefined,
          bms_type: conf?.bms_type ?? undefined,
          //@ts-ignore
          payload: conf?.mission_plan?.payload?.map(s => s.snr_label) ?? undefined,
        }),
        ...g,
      }
      if (configGli.end_battery_percent < -1000) configGli.end_battery_percent = -1000
      if (configGli.end_battery_percent > 100) configGli.end_battery_percent = 100
      return configGli
    })
    if (conf?.processing_mode == SimulationMode.coverage) {
    } else {
      //@ts-ignore
      config.mission_plan = MissionPlanUiState.parseMissionPlan(JSON.stringify(config.mission_plan))
    }

    return config
  }

  static createAloDbMissionGlobalArray(datas: any) {
    return new AloBaseArray(AloDbMissionGlobal, datas)
  }

  static createAloDbMissionGlobalWithDetail(data: any) {
    return new AloDbMissionGlobal({ ...data, detailLoaded: true })
  }
}
